import { SIMPLE_CABLE_SYSTEMS, ADDON_CAVE_BUNDLES } from "./caveConstants"
import { getBuyRequestMeta, getSimpleForBuyRequest } from "@/js/helpers/buy-request"
import type { Product, IRogue, BuyRequest } from "@/js/services/RogueTypes"
import { useProductStore } from "@/js/stores/productStore"
import { isEnabledCustomProduct } from "@/js/customProducts/registry"

export function findCaveCableProduct(rogue: IRogue, mainBuyRequest: BuyRequest, bundleTitle: string, onlyIfSelected = false): Product | undefined {
  try {
    // Find the bundle buy request itself.
    // The main buy request may be a group - where the first child is the bundle, and the rest are accessories
    let bundleBuyRequest = mainBuyRequest
    let bundleProduct = rogue.getProductById(bundleBuyRequest?.productId)

    while (bundleProduct && bundleProduct.type !== 'bundle' && bundleBuyRequest.children) {
      bundleBuyRequest = bundleBuyRequest.children[0]
      bundleProduct = rogue.getProductById(bundleBuyRequest?.productId)
    }

    if (!bundleProduct) {
      return undefined
    }

    // Find the optionId on the bundle product that we are looking to reference
    const bundleOptId = bundleProduct.bundleOptions?.find(opt => opt.title.toLowerCase().includes(bundleTitle.toLowerCase()))?.id

    // Find the customer's selection from their active RHPA
    const buyRequest = bundleBuyRequest.children?.find(child => child.metadata?.bundleOptionId === bundleOptId)

    const caveSimpleCableSystems = SIMPLE_CABLE_SYSTEMS.includes(rogue.getProductById(Number(buyRequest?.productId))?.sku)
    // LP2 & CT-1 are simple products and don't follow the other bundle option patterns.
    // Only mutate when children is non-empty — assigning a new [] reference on every call
    // triggers Vue reactivity unconditionally and causes an infinite update loop.
    if (ADDON_CAVE_BUNDLES.includes(bundleProduct.sku) && caveSimpleCableSystems && buyRequest!.children?.length) {
      buyRequest!.children = []
    }


    if (buyRequest && bundleOptId && (!onlyIfSelected || buyRequest.qty > 0)) {
      return getSimpleForBuyRequest(buyRequest, rogue)
    }

    return undefined
  } catch (e) {
    rogue.log.warn('Error finding bundle product', e as Error)

    return undefined
  }
}

export function isTheCave(rogue: IRogue, item: BuyRequest) {
  const product = rogue.getProductById(item.productId)

  return isEnabledCustomProduct(rogue, 'cave', product)
}

// MARK: PDF
function configureCaveChildren(rogue: IRogue, caveItems: (BuyRequest & { [key: string]: any })[]) {
  const children: any[] = []

  const PANEL_LEFT_SKU  = 'MONSTER-CAVE-2-0-PANEL-LEFT'
  const PANEL_RIGHT_SKU = 'MONSTER-CAVE-2-0-PANEL-RIGHT'
  const RACK_SKU        = 'MONSTER-CAVE-2-0-RACK'
  const FOLD_RACK_SKU   = 'MONSTER-CAVE-2-0-FOLD-RACK'

  // Cave ships as separate bundles (Rack / Left / Right) sharing a recipe, each carrying one
  // section-delimiter child (RACK/FOLD-RACK, PANEL-LEFT, PANEL-RIGHT). Flatten all so sectioning is self-contained.
  const bundle         = caveItems[0]
  const bundleChildren = caveItems.flatMap(item => item.children || [])
  const bundleMeta     = getBuyRequestMeta(bundle, rogue)

  // Walk children once, using each panel/rack SKU as a section-starter.
  // This is order-agnostic — the array can arrive in any sequence.
  const sectionMap: Record<string, BuyRequest[]> = {
    'Rack'       : [],
    'Left Side'  : [],
    'Right Side' : [],
  }
  const sectionProducts: Record<string, BuyRequest> = {}
  let currentSection: string | null = null

  for (const child of bundleChildren) {
    const sku = rogue.getProductById(child.productId)?.sku

    if (sku === PANEL_RIGHT_SKU) { currentSection = 'Right Side'; sectionProducts['Right Side'] = child; continue }
    if (sku === PANEL_LEFT_SKU) { currentSection = 'Left Side'; sectionProducts['Left Side'] = child; continue }
    if (sku === RACK_SKU || sku === FOLD_RACK_SKU) { currentSection = 'Rack'; sectionProducts['Rack'] = child; continue }

    if (currentSection) sectionMap[currentSection].push(child)
  }

  const sections = [
    { label: 'Rack', items: sectionMap['Rack'] },
    { label: 'Left Side', items: sectionMap['Left Side'] },
    { label: 'Right Side', items: sectionMap['Right Side'] },
  ]

  for (const section of sections) {
    if (!section.items.length) continue

    const sectionProduct = sectionProducts[section.label]
    const sectionSku = sectionProduct ? getSimpleForBuyRequest(sectionProduct, rogue)?.sku : undefined

    children.push({
      ...bundleMeta,
      ...bundle,
      sku      : sectionSku,
      name     : `**${section.label}`, // ** indicates that this line in the PDF needs to be bold
      price    : undefined,
      subtotal : undefined,
    })

    for (const child of section.items) {
      const childMeta    = getBuyRequestMeta(child, rogue)
      const childProduct = rogue.getProductById(child.productId)

      if (!childProduct?.name) continue

      children.push({
        ...childMeta,
        ...child,
        sku      : child.sku || childMeta.sku,
        name     : `${childProduct.name.replace('The Monster Cave 2.0 -', '')}`,
        price    : undefined,
        subtotal : undefined,
      })
    }
  }

  return children
}

// Creates a fake Cave parent to have all items listed under.
// Groups by metadata.recipe so multiple cave configurations each get their own parent.
export function groupCaveItems(rogue: IRogue, orderCopy: BuyRequest & { [key: string]: any }) {
  const caveItems: (BuyRequest & { [key: string]: any })[] = []
  const nonCaveItems: BuyRequest[] = []

  for (const item of orderCopy.items) {
    if (isTheCave(rogue, item)) {
      caveItems.push(item)
    } else {
      nonCaveItems.push(item)
    }
  }

  if (caveItems.length === 0) return

  const productStore = useProductStore(rogue.pinia)

  // Group by recipe ID — each cave configuration has a unique recipe
  const recipeGroups: Record<string, (BuyRequest & { [key: string]: any })[]> = {}

  for (const item of caveItems) {
    const recipe = item.metadata?.recipe || 'default'
    if (!recipeGroups[recipe]) recipeGroups[recipe] = []
    recipeGroups[recipe].push(item)
  }

  const caveParents = Object.values(recipeGroups).map(groupItems => {
    let totalWeight   = 0
    let totalSubtotal = 0

    for (const caveBundle of groupItems) {
      const bundleChildren = caveBundle.children || []
      for (const child of bundleChildren) {
        totalSubtotal += child.price || 0
        totalWeight += productStore.products?.[child.productId]?.weight || 0
      }
    }

    return {
      ...groupItems[0],
      productId       : groupItems[0].productId,
      name            : 'The Cave 2.0',
      sku             : undefined,
      price           : undefined,
      rawCaveSubtotal : totalSubtotal,
      caveSubtotal    : rogue.currency(totalSubtotal, { currency: orderCopy.currency }),
      caveWeight      : totalWeight,
      children        : configureCaveChildren(rogue, groupItems),
      isCaveParent    : true,
    }
  })

  orderCopy.items = [...caveParents, ...nonCaveItems]
}
// Helper function to identify the fake parent made for the PDF
export function isCaveParent(item: BuyRequest & { [key: string]: any }): boolean {
  return item.isCaveParent === true
}

// MARK: Mini Feet
export function updateCaveMiniFeetsForCableChange(
  rogue: IRogue,
  bundleBuyRequest: BuyRequest,
  bundleProduct: Product,
  products: Record<number, Product>,
  inputCode: string,
  cableProductId: string
) {
  if (inputCode !== 'leftcablesystem' && inputCode !== 'rightcablesystem') return

  const feetOption = bundleProduct?.bundleOptions?.find(o => o.title.toLowerCase().includes('mini feet'))
  if (!feetOption) return

  const feetBuyRequest = bundleBuyRequest.children?.find(c => c.metadata?.bundleOptionId === feetOption.id)
  if (!feetBuyRequest) return

  const cableSelected = cableProductId !== ''

  if (cableSelected) {
    feetBuyRequest.qty = 0

    return
  }

  // Cable cleared — only add feet if the other side also has no cable
  const otherSideKey = inputCode === 'leftcablesystem' ? 'right cable system' : 'left cable system'
  const otherSideCableActive = !!findCaveCableProduct(rogue, bundleBuyRequest, otherSideKey, true)?.name

  if (otherSideCableActive) return

  const targetFeetProduct = feetOption?.values.find(v =>
    products[v.productId]?.name?.toLowerCase().includes('mini feet')
  )

  if (targetFeetProduct) {
    feetBuyRequest.productId = targetFeetProduct.productId
    feetBuyRequest.qty = 1
  }
}